/**
 * Push notification service: request permission, get FCM token, register with backend, handle foreground messages.
 */
import { getFirebaseMessaging, vapidKey } from "../firebase";

const getBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
};

/**
 * Request notification permission and get FCM token; then register token with backend.
 * Call after user is logged in (so auth token is available).
 * @param {string} authToken - JWT Bearer token
 * @returns {Promise<string|null>} FCM token or null
 */
export async function requestPermissionAndRegister(authToken) {
  if (!authToken) return null;
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("[Push] Notification permission denied.");
      return null;
    }

    const token = await getToken(messaging);
    if (!token) return null;

    await registerTokenWithBackend(token, authToken);
    return token;
  } catch (e) {
    console.error("[Push] requestPermissionAndRegister error:", e);
    return null;
  }
}

/**
 * Wait until the service worker is activated (required for PushManager.subscribe).
 */
function waitForActive(registration) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(registration), 8000);
    function check() {
      if (registration.active && registration.active.state === "activated") {
        clearTimeout(timeout);
        resolve(registration);
        return;
      }
      if (registration.installing) {
        registration.installing.addEventListener("statechange", check);
      }
      if (registration.waiting) {
        registration.waiting.addEventListener("statechange", check);
      }
    }
    check();
    if (registration.active && registration.active.state === "activated") {
      clearTimeout(timeout);
      resolve(registration);
    }
  });
}

/**
 * Register the Firebase messaging service worker and wait until it's active.
 */
async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
    });
    await registration.ready;
    await waitForActive(registration);
    return registration;
  } catch (e) {
    console.error("[Push] Service worker registration error:", e);
    return null;
  }
}

/**
 * Get FCM token only (no backend register). Use when sending token with login.
 * Requests permission and returns token or null.
 */
export async function getFCMTokenOnly() {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    return await getToken(messaging);
  } catch (e) {
    console.warn("[Push] getFCMTokenOnly error:", e);
    return null;
  }
}

/**
 * Get FCM token (requires VAPID key for web). Registers SW first to avoid timeout.
 */
async function getToken(messaging) {
  const { getToken } = await import("firebase/messaging");
  try {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      console.warn("[Push] No service worker registration.");
      return null;
    }
    const token = await getToken(messaging, {
      vapidKey: vapidKey || undefined,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (e) {
    console.error("[Push] getToken error:", e);
    return null;
  }
}

/**
 * Register FCM token with our backend so we can send push to this device.
 */
async function registerTokenWithBackend(fcmToken, authToken) {
  const res = await fetch(getBaseUrl() + "/api/push/register", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      fcmToken,
      platform: "web",
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || "Failed to register push token");
  }
}

/**
 * Unregister token (e.g. on logout).
 */
export async function unregisterToken(fcmToken, authToken) {
  if (!fcmToken || !authToken) return;
  try {
    await fetch(getBaseUrl() + "/api/push/unregister", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ fcmToken }),
    });
  } catch (e) {
    console.error("[Push] unregister error:", e);
  }
}

/**
 * Subscribe to foreground messages. Handler receives payload when app is in focus.
 * @param {(payload: import('firebase/messaging').MessagePayload) => void} onMessageHandler
 * @returns {() => void} Unsubscribe function
 */
export async function onForegroundMessage(onMessageHandler) {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return () => {};
    const { onMessage } = await import("firebase/messaging");
    return onMessage(messaging, onMessageHandler);
  } catch (e) {
    console.error("[Push] onForegroundMessage setup error:", e);
    return () => {};
  }
}
